// pages/FamilyDashboard.js - Complete rewrite with conditional layout
import React, { useState, useEffect } from 'react';
import { Star, BookOpen, Plus, Sprout, Leaf, TreePine, Users, UserPlus, Copy, Check, Settings, LogOut, Eye, TrendingUp, Coins, Target, Clock, CheckCircle, XCircle, Camera, Upload } from 'lucide-react';
import { Button, Card } from '../components/ui';
import { ProofUploadModal } from '../components/ProofUploadModal';
import { FamilyDashboardSection } from '../components/EnhancedChildCards';

export const FamilyDashboard = ({
  family,
  guardian,
  children = [],
  guardians = [],
  onNavigate,
  onLogout,
  onGenerateInvite
}) => {
  // State management
  const [showInviteCode, setShowInviteCode] = useState(false);
  const [inviteCode, setInviteCode] = useState('ABC123');
  const [copied, setCopied] = useState(false);
  const [pendingDeposits, setPendingDeposits] = useState([]);
  const [loadingDeposits, setLoadingDeposits] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [activeInvite, setActiveInvite] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [depositToReject, setDepositToReject] = useState(null);
  const [rejectStatus, setRejectStatus] = useState('confirm');
  const [rejectError, setRejectError] = useState('');
  const [showProofModal, setShowProofModal] = useState(false);
  const [selectedDeposit, setSelectedDeposit] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successData, setSuccessData] = useState(null);

  const API_BASE_URL = 'https://growthsprouts-server.vercel.app/api';

  // Generate invite code
  const handleGenerateInvite = async (inviteType = 'guardian') => {
    try {
      console.log('Generating invite code for family:', family.id, 'type:', inviteType);

      const response = await fetch(`${API_BASE_URL}/families/${family.id}/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          guardianId: guardian.id,
          inviteType: inviteType,
          expiresIn: 7 * 24 * 60 * 60 * 1000 // 7 days
        })
      });

      const result = await response.json();

      if (result.success) {
        console.log('Invite code generated:', result.data);
        setActiveInvite({
          code: result.data.code,
          expiresAt: new Date(result.data.expiresAt),
          uses: result.data.uses,
          inviteType: result.data.inviteType
        });
      } else {
        console.error('Failed to generate invite code:', result.error);
        alert('Failed to generate invite code. Please try again.');
      }
    } catch (error) {
      console.error('Error generating invite code:', error);
      alert('Error generating invite code. Please try again.');
    }
  };

  const handleCopyLink = async () => {
  try {
    await navigator.clipboard.writeText(`https://growthsprouts.ai/invite/${activeInvite.code}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  } catch (error) {
    console.error('Failed to copy:', error);
  }
};

  // Copy invite code
  const handleCopyInvite = () => {
    if (activeInvite) {
      navigator.clipboard.writeText(activeInvite.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Format time until expiry
  const formatTimeUntilExpiry = (expiresAt) => {
    const now = new Date();
    const timeLeft = expiresAt - now;

    if (timeLeft <= 0) return 'Expired';

    const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

    if (days > 0) {
      return `${days}d ${hours}h remaining`;
    } else if (hours > 0) {
      return `${hours}h remaining`;
    } else {
      const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
      return `${minutes}m remaining`;
    }
  };

  // Load pending deposits
  useEffect(() => {
    const loadPendingDeposits = async () => {
      if (!family?.id) return;

      try {
        setLoadingDeposits(true);
        console.log('FamilyDashboard: Loading pending deposits for family:', family.id);

        const response = await fetch(`${API_BASE_URL}/deposits/pending/${family.id}`);
        const result = await response.json();

        if (result.success) {
          console.log('FamilyDashboard: Loaded pending deposits:', result.data);
          setPendingDeposits(result.data || []);
        } else {
          console.error('FamilyDashboard: Failed to load pending deposits:', result.error);
        }
      } catch (error) {
        console.error('FamilyDashboard: Error loading pending deposits:', error);
      } finally {
        setLoadingDeposits(false);
      }
    };

    loadPendingDeposits();

    // Refresh every 30 seconds to check for new deposits
    const interval = setInterval(loadPendingDeposits, 30000);
    return () => clearInterval(interval);
  }, [family?.id]);

  // Delete invite code
  const handleDeleteInvite = async () => {
    try {
      console.log('Deactivating invite code for family:', family.id);

      const response = await fetch(`${API_BASE_URL}/families/${family.id}/active-invite`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const result = await response.json();

      if (result.success) {
        console.log('Invite code deactivated successfully');
        setActiveInvite(null);
      } else {
        console.error('Failed to deactivate invite code:', result.error);
        alert('Failed to deactivate invite code. Please try again.');
      }
    } catch (error) {
      console.error('Error deactivating invite code:', error);
      alert('Error deactivating invite code. Please try again.');
    }
  };

  const refreshChildrenData = () => {
  console.log('FamilyDashboard: Refresh requested');
  return true; // The actual refresh happens in App.js
};
  

  // Load active invite code
  useEffect(() => {
    const loadActiveInviteCode = async () => {
      if (!family?.id) return;

      try {
        console.log('Loading active invite code for family:', family.id);

        const response = await fetch(`${API_BASE_URL}/families/${family.id}/active-invite`);
        const result = await response.json();

        if (result.success && result.data) {
          console.log('Found active invite code:', result.data);
          setActiveInvite({
            code: result.data.code,
            expiresAt: new Date(result.data.expiresAt),
            uses: result.data.uses,
            inviteType: result.data.inviteType
          });
        } else {
          console.log('No active invite code found');
          setActiveInvite(null);
        }
      } catch (error) {
        console.error('Error loading active invite code:', error);
      }
    };

    loadActiveInviteCode();
  }, [family?.id]);

  // Handle proof upload
  const handleUploadProof = (deposit) => {
    setSelectedDeposit(deposit);
    setShowProofModal(true);
  };

  // Handle proof submission success
  const handleProofSubmitted = (updatedDeposit) => {
    // Remove deposit from pending list since it's now awaiting admin approval
    setPendingDeposits(prev => prev.filter(d => d.id !== updatedDeposit.id));

    // Close proof modal
    setShowProofModal(false);
    
    // Set success data and show success modal instead of browser alert
    setSuccessData({
      childName: selectedDeposit?.first_name || 'Child',
      amount: selectedDeposit?.amount || 0,
      depositId: updatedDeposit.id
    });
    setShowSuccessModal(true);
  };

  // Confirm reject deposit
  const confirmRejectDeposit = async () => {
    if (!depositToReject) return;

    setRejectStatus('loading');

    try {
      console.log('FamilyDashboard: Rejecting deposit:', depositToReject.id);

      const response = await fetch(`${API_BASE_URL}/deposits/${depositToReject.id}/verify`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          approved: false,
          guardian_id: guardian.id
        }),
      });

      const result = await response.json();

      if (result.success) {
        console.log('FamilyDashboard: Deposit rejected successfully:', result.data);

        // Remove the rejected deposit from pending list
        setPendingDeposits(prev => prev.filter(d => d.id !== depositToReject.id));

        // Show success state
        setRejectStatus('success');
      } else {
        console.error('FamilyDashboard: Failed to reject deposit:', result.error);
        setRejectError(result.error || 'Failed to reject deposit. Please try again.');
        setRejectStatus('error');
      }
    } catch (error) {
      console.error('FamilyDashboard: Error rejecting deposit:', error);
      setRejectError('Error rejecting deposit. Please try again.');
      setRejectStatus('error');
    }
  };

  // Handle deposit rejection
  const handleDepositReject = (deposit) => {
    setDepositToReject(deposit);
    setRejectStatus('confirm');
    setRejectError('');
    setShowRejectModal(true);
  };

  // Calculate stats with bonus structure
  const calculateBonus = (amount) => {
    if (amount <= 1000) {
      return amount * 0.05;
    } else {
      const firstThousand = 1000 * 0.05;
      const remainder = Math.min(amount - 1000, 1000) * 0.10;
      return firstThousand + remainder;
    }
  };

  const totalFamilySavings = children.reduce((total, child) => total + (child.total_saved || child.totalSaved || 0), 0);
  const totalFamilyBonus = calculateBonus(totalFamilySavings);
  const totalChildren = children.length;
  const totalGuardians = guardians.length || 1;
  const averageSavings = totalChildren > 0 ? totalFamilySavings / totalChildren : 0;
  const isNewFamily = totalChildren === 0;

  // Format guardian name
  const guardianName = guardian?.first_name || guardian?.firstName || 'Guardian';
  const familyName = family?.name || family?.familyName || 'Your Family';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-green-50/30 to-emerald-50/50">
      {/* Header */}
      <div className="border-b border-gray-200/80 bg-white/70 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 sm:space-x-4 min-w-0 flex-1">
              <img src="/logo.svg" alt="GrowthSprouts" className="w-10 h-10 sm:w-12 sm:h-12 flex-shrink-0" />
              <div className="text-left min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">
                    {family?.name}
                  </h1>
                  {family?.program && (
                    <div className="inline-flex items-center px-2 sm:px-2.5 py-0.5 sm:py-1 bg-gradient-to-r from-emerald-500 to-green-600 rounded-full shadow-sm">
                      <Leaf className="w-3 h-3 mr-1 text-white" />
                      <span className="text-xs font-medium text-white truncate max-w-[100px] sm:max-w-none">
                        {family.program}
                      </span>
                    </div>
                  )}
                </div>
                <div className="text-left text-xs sm:text-sm text-gray-600">Welcome back, {guardian?.first_name}</div>
              </div>
            </div>

            <div className="flex items-center space-x-2 sm:space-x-3 flex-shrink-0">
              {/* Ledger Book Button */}
              <button
                onClick={() => onNavigate('ledgerBook')}
                className="flex items-center space-x-0 sm:space-x-2 px-2 sm:px-4 py-2 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors shadow-sm"
                title="View Family Ledger Book"
              >
                <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
                <span className="hidden sm:inline text-sm font-medium text-gray-700">Ledger Book</span>
              </button>

              {/* Invite Button */}
              <button
                onClick={() => setShowInviteModal(true)}
                className="flex items-center space-x-0 sm:space-x-2 px-2 sm:px-4 py-2 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors shadow-sm"
                title="Invite Family Member"
              >
                <UserPlus className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
                <span className="hidden sm:inline text-sm font-medium text-gray-700">Invite</span>
              </button>

              {/* Logout Button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={onLogout}
                className="rounded-xl p-2 sm:px-3"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Conditional Top Section - Only show if there's content */}
        {(pendingDeposits.length > 0 || activeInvite) && (
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row gap-6 items-stretch">

              {/* Pending Deposits Panel - Only show if there are pending deposits */}
              {pendingDeposits.length > 0 && (
                <Card className="overflow-hidden shadow-lg border-l-4 border-l-orange-400 flex-1">
                  <div className="p-4 sm:p-6">
                    <div className="flex items-start justify-between mb-4 gap-3">
                      <div className="flex items-start space-x-3 min-w-0 flex-1">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="font-bold text-gray-900 text-sm sm:text-base">
                            {pendingDeposits.length} Pending Deposit{pendingDeposits.length !== 1 ? 's' : ''}
                          </h3>
                          <p className="text-xs sm:text-sm text-gray-600 leading-tight">
                            Your children have requested to add money to their gardens
                          </p>
                        </div>
                      </div>
                      <div className="bg-orange-100 text-orange-800 px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium flex-shrink-0">
                        Action Required
                      </div>
                    </div>

                    <div className="space-y-3">
                      {pendingDeposits.slice(0, 2).map((deposit) => (
                        <div key={deposit.id} className="p-3 sm:p-4 bg-gray-50 rounded-xl">
                          <div className="flex items-start justify-between mb-3 gap-3">
                            <div className="flex items-start space-x-3 min-w-0 flex-1">
                              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                                <Sprout className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="font-medium text-gray-900 text-sm sm:text-base truncate">
                                  {deposit.first_name}
                                </div>
                                <div className="text-xs sm:text-sm text-gray-600">Earned Money</div>
                                <div className="text-xs text-gray-500">
                                  {new Date(deposit.created_at).toLocaleDateString()}
                                </div>
                              </div>
                            </div>
                            
                            {/* Amount Badge */}
                            <div className="flex-shrink-0">
                              <div className="bg-emerald-100 text-emerald-800 px-3 py-1.5 rounded-lg font-bold text-sm sm:text-base border border-emerald-200">
                                ${deposit.amount}
                              </div>
                            </div>
                          </div>
                          
                          {/* Mobile: Stacked buttons, Desktop: Side by side */}
                          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 w-full">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDepositReject(deposit)}
                              className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50 h-9 text-xs sm:text-sm"
                            >
                              <XCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                              Reject
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => {
                                setSelectedDeposit(deposit);
                                setShowProofModal(true);
                              }}
                              className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white h-9 text-xs sm:text-sm"
                            >
                              <Upload className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                              Upload Proof
                            </Button>
                          </div>
                        </div>
                      ))}

                      {pendingDeposits.length > 2 && (
                        <div className="text-center text-xs sm:text-sm text-gray-500 pt-2">
                          +{pendingDeposits.length - 2} more pending deposits
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              )}

              {/* Active Invite Panel - Only show if there's an active invite */}
              {activeInvite && (
                <Card className="overflow-hidden shadow-lg border-l-4 border-l-blue-400 flex-1">
                  <div className="p-6 h-full flex flex-col">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                          <UserPlus className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="font-bold text-gray-900">Active Invite Code</h3>
                          <p className="text-sm text-gray-600">Share with family member</p>
                        </div>
                      </div>
                      <button
                        onClick={handleDeleteInvite}
                        className="text-gray-400 hover:text-red-500 transition-colors duration-200"
                      >
                        <XCircle className="w-5 h-5" />
                      </button>
                    </div>

                    {/* Invite Code Display */}
                    <div className="bg-blue-50 rounded-xl p-4 mb-4">
                      <div className="flex items-center justify-between">
                        <div className="font-mono text-2xl font-bold text-blue-600 tracking-widest">
                          {activeInvite.code}
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={handleCopyInvite}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-100"
                        >
                          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>

                    {/* Stats Row */}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="text-center p-3 bg-gray-50 rounded-xl">
                        <div className="text-lg font-bold text-gray-900">{formatTimeUntilExpiry(activeInvite.expiresAt)}</div>
                        <div className="text-xs text-gray-600">Time Left</div>
                      </div>
                      <div className="text-center p-3 bg-gray-50 rounded-xl">
                        <div className="text-lg font-bold text-gray-900">{activeInvite.uses}</div>
                        <div className="text-xs text-gray-600">Times Used</div>
                      </div>
                    </div>

                    {/* Share Link */}
<div className="mt-4 pt-4 border-t border-gray-200">
  <div className="flex flex-col space-y-2">
    <div className="flex-1 min-w-0">
      <div className="text-xs text-gray-500 mb-1">Invite Link</div>
      <div className="text-xs font-mono text-gray-700 break-all leading-relaxed">
        https://growthsprouts.ai/invite/{activeInvite.code}
      </div>
    </div>
    <div className="flex justify-end">
      <button
        onClick={handleCopyLink}
        className={`text-xs font-medium transition-colors px-3 py-1 rounded ${
          copied 
            ? 'text-green-600 bg-green-50' 
            : 'text-blue-600 hover:text-blue-700 hover:bg-blue-50'
        }`}
      >
        {copied ? '✓ Copied!' : 'Copy Link'}
      </button>
    </div>
  </div>
</div>
                  </div>
                </Card>
              )}

            </div>
          </div>
        )}

        {/* Hero Stats Section */}
        <div className="mb-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Primary Stat */}
            <div className="md:col-span-2">
              <div className="bg-gradient-to-br from-emerald-500 to-green-600 rounded-3xl p-8 text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-white/10 backdrop-blur-3xl"></div>
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
                      <Coins className="w-8 h-8 text-white" />
                    </div>
                    <div className="text-right">
                      <div className="text-white/80 text-sm font-medium">Total Family</div>
                      <div className="text-white/60 text-xs">Savings Balance</div>
                    </div>
                  </div>
                  <div className="text-4xl font-bold mb-2">${totalFamilySavings.toFixed(2)}</div>
                  <div className="text-emerald-100 text-sm">
                    {totalFamilySavings > 0
                      ? `+$${totalFamilyBonus.toFixed(2)} total bonus earned`
                      : 'Ready to start growing!'
                    }
                  </div>
                </div>
                {/* Decorative circles */}
                <div className="absolute -top-6 -right-6 w-24 h-24 bg-white/10 rounded-full"></div>
                <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-white/5 rounded-full"></div>
              </div>
            </div>

            {/* Secondary Stats */}
            <div className="space-y-6">
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50 shadow-lg">
                <div className="flex items-center justify-between">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-gray-900">{totalChildren}</div>
                    <div className="text-gray-600 text-sm">Children</div>
                  </div>
                </div>
              </div>

              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50 shadow-lg">
                <div className="flex items-center justify-between">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-gray-900">${averageSavings.toFixed(0)}</div>
                    <div className="text-gray-600 text-sm">Avg per Child</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {isNewFamily ? (
          /* Getting Started Section */
          <div className="space-y-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-4">
                🌱 Start Your Family's Growth Journey
              </h2>
              <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                Create savings accounts for your children and watch their financial literacy bloom
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {/* Add First Child */}
              <div
                className="group cursor-pointer"
                onClick={() => onNavigate('addChild')}
              >
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 border-2 border-dashed border-emerald-300 hover:border-emerald-400 transition-all duration-300 hover:shadow-2xl hover:-translate-y-1">
                  <div className="text-center">
                    <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-emerald-500 to-green-600 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg">
                      <Plus className="w-10 h-10 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">Add Your First Child</h3>
                    <p className="text-gray-600 mb-6 leading-relaxed">Create a personalized savings garden and start their journey to financial independence</p>
                    <Button className="w-full rounded-xl bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white border-0 shadow-lg">
                      <Plus className="w-5 h-5 mr-2" />
                      Create First Garden
                    </Button>
                  </div>
                </div>
              </div>

              {/* Invite Guardian */}
              <div
                className="group cursor-pointer"
                onClick={() => setShowInviteModal(true)}
              >
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 border-2 border-dashed border-blue-300 hover:border-blue-400 transition-all duration-300 hover:shadow-2xl hover:-translate-y-1">
                  <div className="text-center">
                    <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg">
                      <UserPlus className="w-10 h-10 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">Invite Co-Guardian</h3>
                    <p className="text-gray-600 mb-6 leading-relaxed">Add your partner or trusted family member to help manage your children's savings together</p>
                    <Button variant="secondary" className="w-full rounded-xl border-2 border-blue-200 hover:border-blue-300 text-blue-700 hover:text-blue-800 bg-blue-50 hover:bg-blue-100">
                      <UserPlus className="w-5 h-5 mr-2" />
                      Generate Invite
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Children Dashboard */
          <div className="space-y-8">
  <FamilyDashboardSection 
    children={children} 
    onNavigate={onNavigate}
    onRefreshNeeded={refreshChildrenData}
  />
</div>
        )}
      </div>

      {/* Proof Upload Modal */}
      <ProofUploadModal
        isOpen={showProofModal}
        onClose={() => setShowProofModal(false)}
        deposit={selectedDeposit}
        guardian={guardian}
        onProofSubmitted={handleProofSubmitted}
      />

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl border border-gray-200">
            <h4 className="text-xl font-bold text-gray-900 mb-6">Invite Family Member</h4>

            {!activeInvite ? (
              <div className="space-y-6">
                <p className="text-gray-600 text-sm">
                  Generate a secure code to invite your partner or trusted family member to help manage your children's savings
                </p>

                {/* Invite Type Selection */}
                <div className="space-y-3">
                  <p className="text-sm font-medium text-gray-700">Who are you inviting?</p>
                  <div className="grid grid-cols-1 gap-3">
                    <button
                      onClick={() => {
                        handleGenerateInvite('guardian');
                        setShowInviteModal(false);
                      }}
                      className="p-4 border-2 border-blue-200 hover:border-blue-300 bg-blue-50 hover:bg-blue-100 rounded-xl transition-all duration-200 text-left"
                    >
                      <div className="flex items-center space-x-3">
                        <Users className="w-6 h-6 text-blue-600" />
                        <div>
                          <div className="text-sm font-medium text-blue-700">Parent/Guardian</div>
                          <div className="text-xs text-blue-600">Adult family member</div>
                        </div>
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        handleGenerateInvite('child');
                        setShowInviteModal(false);
                      }}
                      className="p-4 border-2 border-green-200 hover:border-green-300 bg-green-50 hover:bg-green-100 rounded-xl transition-all duration-200 text-left"
                    >
                      <div className="flex items-center space-x-3">
                        <Users className="w-6 h-6 text-green-600" />
                        <div>
                          <div className="text-sm font-medium text-green-700">Child</div>
                          <div className="text-xs text-green-600">Kid in the family</div>
                        </div>
                      </div>
                    </button>
                  </div>
                </div>

                <Button
                  variant="ghost"
                  onClick={() => setShowInviteModal(false)}
                  className="w-full rounded-xl"
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-green-800 font-medium text-sm">
                    You already have an active invite code! It's displayed at the top of your dashboard.
                  </p>
                </div>
                <Button
                  variant="ghost"
                  onClick={() => setShowInviteModal(false)}
                  className="w-full rounded-xl"
                >
                  Close
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            {/* Header */}
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center space-x-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  rejectStatus === 'success' ? 'bg-green-100' :
                  rejectStatus === 'error' ? 'bg-red-100' : 'bg-red-100'
                }`}>
                  {rejectStatus === 'success' ? (
                    <div className="w-5 h-5 text-green-600">✓</div>
                  ) : rejectStatus === 'loading' ? (
                    <div className="w-5 h-5 text-red-600 animate-spin">⟳</div>
                  ) : (
                    <XCircle className="w-5 h-5 text-red-600" />
                  )}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {rejectStatus === 'success' ? 'Deposit Rejected' :
                     rejectStatus === 'loading' ? 'Rejecting Deposit...' :
                     rejectStatus === 'error' ? 'Rejection Failed' :
                     'Reject Deposit Request'}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {rejectStatus === 'success' ? 'The request has been removed' :
                     rejectStatus === 'loading' ? 'Please wait...' :
                     rejectStatus === 'error' ? 'Something went wrong' :
                     'This action cannot be undone'}
                  </p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              {/* Deposit Details */}
              <div className="mb-6">
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                      <Sprout className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">
                        {depositToReject?.first_name} - ${depositToReject?.amount}
                      </div>
                      <div className="text-sm text-gray-600">Earned Money</div>
                      <div className="text-xs text-gray-500">
                        {depositToReject && new Date(depositToReject.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Status Messages */}
              {rejectStatus === 'confirm' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start space-x-3">
                    <div className="w-5 h-5 text-red-600 mt-0.5">⚠️</div>
                    <div>
                      <p className="text-red-800 font-medium text-sm">Are you sure you want to reject this deposit?</p>
                      <p className="text-red-700 text-sm mt-1">
                        This will permanently remove the request and {depositToReject?.first_name} will be notified.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {rejectStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start space-x-3">
                    <div className="w-5 h-5 text-green-600 mt-0.5">✅</div>
                    <div>
                      <p className="text-green-800 font-medium text-sm">Deposit request rejected</p>
                      <p className="text-green-700 text-sm mt-1">
                        {depositToReject?.first_name} has been notified that their request was not approved.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {rejectStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start space-x-3">
                    <div className="w-5 h-5 text-red-600 mt-0.5">❌</div>
                    <div>
                      <p className="text-red-800 font-medium text-sm">Failed to reject deposit</p>
                      <p className="text-red-700 text-sm mt-1">
                        {rejectError || 'Please try again or contact support if the issue persists.'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {rejectStatus === 'loading' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-5 h-5 text-blue-600 animate-spin">⟳</div>
                    <div>
                      <p className="text-blue-800 font-medium text-sm">Processing rejection...</p>
                      <p className="text-blue-700 text-sm mt-1">Please wait while we process your request.</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              {rejectStatus === 'confirm' && (
                <div className="flex space-x-3">
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setShowRejectModal(false);
                      setDepositToReject(null);
                      setRejectStatus('confirm');
                    }}
                    className="flex-1 rounded-xl border border-gray-200 hover:bg-gray-50"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={confirmRejectDeposit}
                    className="flex-1 rounded-xl bg-red-600 hover:bg-red-700 text-white"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Reject Deposit
                  </Button>
                </div>
              )}

              {rejectStatus === 'error' && (
                <div className="flex space-x-3">
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setShowRejectModal(false);
                      setDepositToReject(null);
                      setRejectStatus('confirm');
                      setRejectError('');
                    }}
                    className="flex-1 rounded-xl border border-gray-200 hover:bg-gray-50"
                  >
                    Close
                  </Button>
                  <Button
                    onClick={confirmRejectDeposit}
                    className="flex-1 rounded-xl bg-red-600 hover:bg-red-700 text-white"
                  >
                    Try Again
                  </Button>
                </div>
              )}

              {(rejectStatus === 'loading' || rejectStatus === 'success') && (
                <div className="flex justify-center">
                  {rejectStatus === 'success' && (
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setShowRejectModal(false);
                        setDepositToReject(null);
                        setRejectStatus('confirm');
                      }}
                      className="rounded-xl border border-gray-200 hover:bg-gray-50"
                    >
                      Close
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            {/* Header */}
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Proof Submitted Successfully!</h3>
                  <p className="text-sm text-gray-500">Your deposit proof has been sent for review</p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              {/* Deposit Details */}
              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <Sprout className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">
                      {successData?.childName} - ${successData?.amount}
                    </div>
                    <div className="text-sm text-gray-600">Deposit Request</div>
                  </div>
                </div>
              </div>

              {/* Success Message */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 text-green-600 mt-0.5">✅</div>
                  <div>
                    <p className="text-green-800 font-medium text-sm">Proof uploaded successfully!</p>
                    <p className="text-green-700 text-sm mt-1">
                      The deposit is now awaiting admin approval and will be processed within 24 hours.
                    </p>
                  </div>
                </div>
              </div>

              {/* What's Next */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 text-blue-600 mt-0.5">ℹ️</div>
                  <div>
                    <p className="text-blue-800 font-medium text-sm mb-2">What happens next?</p>
                    <div className="text-blue-700 text-xs space-y-1">
                      <p>• Our admin team will review your proof within 24 hours</p>
                      <p>• Once approved, money will be added to {successData?.childName}'s account</p>
                      <p>• You'll receive a notification when it's complete</p>
                      <p>• The child will see their updated balance immediately</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Button */}
              <Button
                onClick={() => {
                  setShowSuccessModal(false);
                  setSuccessData(null);
                  setSelectedDeposit(null);
                }}
                className="w-full rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Got it, thanks!
              </Button>
            </div>
          </div>
        </div>
      )}

      
    </div>
  );
};